import { useMutation, useLazyQuery } from '@apollo/client/react'
import {
  Box,
  Button,
  HStack,
  Heading,
  Icon,
  SimpleGrid,
  Text,
  VStack,
  chakra,
  Separator,
  BoxProps,
  SimpleGridProps,
  Input,
  InputGroup,
  Link,
  Alert,
  IconButton,
  Dialog,
  Field,
  Presence,
} from '@chakra-ui/react'
import { Tooltip } from '@src/components/chakra-ui/tooltip'
import { UserSignupEmailCaptureForm } from '@src/components/sco/Account/UserSignupEmailCaptureForm'
import { AutoResizeTextarea } from '@src/components/sco/AutoResizeTextarea'
import { ComboboxItem } from '@src/components/sco/ComboboxTagInput'
import { CurrencyInput } from '@src/components/sco/CurrencyInput'
import { InvestorTagsComboboxInput } from '@src/components/sco/InvestorTagsComboboxInput'
import { UploadDocumentButton } from '@src/components/sco/UploadDocumentButton'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { SSRGate } from '@src/components/sco/utilities/SSRGate'
import { RegisterUserPayload, ScoInvestorSearch, UpsertInvestorSearchInput } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { TrackEvent } from '@src/lib/TrackEvent'
import { gql } from '@src/lib/gql'
import { scoBackendUrl } from '@src/lib/startups-url'
import tracker from '@src/lib/tracker'
import { useRouter } from 'next/navigation'
import { parseAsBoolean, useQueryState } from 'nuqs'
import React, { useEffect, useState } from 'react'
import { LuArrowRight, LuX, LuUpload, LuFileCheck } from 'react-icons/lu'

type InvestorSearchBuilderProps = {
  containerBoxProps?: BoxProps
  ctaBoxProps?: BoxProps
  defaultFormValues?: {
    pitchDeckUrl?: string
    companyName?: string
    companyElevatorPitch?: string
    fundingGoalInDollars?: number
    requiredInvestorTags?: ComboboxItem[]
  }
  emailCaptureMethod?: 'modal' | 'inline'
  hide?: {
    companyElevatorPitch?: boolean
    companyName?: boolean
    contentBox?: boolean
    contentTitle?: boolean
    formTitle?: boolean
    fundingGoalInDollars?: boolean
    matchCount?: boolean
    matchedInvestors?: boolean
    pitchDeckUpload?: boolean
    requiredInvestorTags?: boolean
  }
  labels?: {
    companyElevatorPitch?: any
    companyName?: any
    contentTitle?: string
    formTitle?: string
    fundingGoalInDollars?: any
  }
  colors?: {
    labels?: BoxProps['color']
  }
  formPlacement?: 'left' | 'right'
  formBoxProps?: BoxProps
  formInnerBoxProps?: BoxProps
  renderContent?: () => any
  simpleGridProps?: SimpleGridProps
  contentBoxProps?: BoxProps
  contentInnerBoxProps?: BoxProps
  investorSearch?: Partial<ScoInvestorSearch>
}

export const InvestorSearchBuilder = (props: InvestorSearchBuilderProps) => {
  const router = useRouter()
  const { currentUser } = useCurrentUser()
  const { getBreakpointValue } = useBreakpointContext()

  /* Toggles/controls/data */
  const isMobileVariant = getBreakpointValue([true, true, false])
  const isFormOnRight = props.formPlacement === 'right'
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isModified, setIsModified] = useState<boolean>(false)
  const [signup, setSignup] = useQueryState<boolean>('signup', parseAsBoolean)
  const [requireSignup, setRequireSignup] = useState<boolean>(signup)
  const [errorMessage, setErrorMessage] = useState<string>('')

  /* Form Fields */
  const [companyName, setCompanyName] = useState<string>(props.defaultFormValues?.companyName || '')
  const [companyElevatorPitch, setCompanyElevatorPitch] = useState<string>(
    props.defaultFormValues?.companyElevatorPitch || ''
  )
  const [fundingGoalInDollars, setFundingGoalInDollars] = useState<number>(
    props.defaultFormValues?.fundingGoalInDollars || null
  )
  const [requiredInvestorTags, setRequiredInvestorTags] = useState<ComboboxItem[]>(
    props.defaultFormValues?.requiredInvestorTags || []
  )
  const [pitchDeckUrl, setPitchDeckUrl] = useState<string>('')

  useEffect(() => {
    const values = [
      companyName,
      companyElevatorPitch,
      fundingGoalInDollars,
      requiredInvestorTags?.length,
      pitchDeckUrl,
    ]

    if (!isModified && values.find(v => Boolean(v))) {
      setIsModified(true)
    }
  }, [
    companyName,
    companyElevatorPitch,
    fundingGoalInDollars,
    requiredInvestorTags?.length,
    pitchDeckUrl,
    isModified,
  ])

  useEffect(() => {
    if (isModified) {
      tracker.investorMatchingBuilderModified()
    }
  }, [isModified])

  /* GraphQL mutation/queries */
  const [upsertInvestorSearch, upsertInvestorSearchMutation] = useMutation(gql`
    mutation ($input: UpsertInvestorSearchInput!) {
      upsertInvestorSearch(input: $input) {
        investorSearch {
          id
          investorSearchId
        }
      }
    }
  `)

  const [runQuery, query] = useLazyQuery(gql`
    query ($investorSearchId: String!) {
      scoInvestorSearch(investorSearchId: $investorSearchId) {
        id
        investorSearchId
        title
        companyElevatorPitch
        companyName
        fundingGoalInDollars
        requiredInvestorTags
        isMatchEmailEnabled
        investorMatches {
          id
          pagination {
            itemCount
          }
        }
      }
    }
  `)
  const investorSearch: ScoInvestorSearch = query.data?.scoInvestorSearch

  useEffect(() => {
    if (props.investorSearch?.investorSearchId) {
      runQuery({
        variables: {
          investorSearchId: props.investorSearch.investorSearchId,
        },
      })
    }
  }, [props.investorSearch?.investorSearchId])

  useEffect(() => {
    if (investorSearch) {
      setCompanyName(investorSearch.companyName)
      setCompanyElevatorPitch(investorSearch.companyElevatorPitch)
      setFundingGoalInDollars(investorSearch.fundingGoalInDollars)
      setRequiredInvestorTags(
        investorSearch.requiredInvestorTags.map(rs => ({
          title: rs[0],
          key: rs[2],
        }))
      )
    }
  }, [investorSearch?.investorSearchId])

  useEffect(() => {
    if (currentUser?.isRegistered) {
      setRequireSignup(false)
    }
  }, [currentUser?.isRegistered])

  const onSubmit = async (isRegistered = false) => {
    setIsSubmitting(true)
    if (!isRegistered && !currentUser?.isRegistered) {
      setRequireSignup(true)
      setIsSubmitting(false)
      return
    }

    setErrorMessage(null)
    if (!pitchDeckUrl && !companyName && !companyElevatorPitch && !fundingGoalInDollars) {
      setErrorMessage('Please provide startup info or pitch deck.')
      setIsSubmitting(false)
      return
    }

    const input: UpsertInvestorSearchInput = {
      investorSearchId: props.investorSearch?.investorSearchId || null,
      requiredInvestorTagTitles: requiredInvestorTags.map(rs => rs.title),
      reprocess: true,
      companyName: companyName,
      companyElevatorPitch: companyElevatorPitch,
      fundingGoalInDollars: fundingGoalInDollars,
      pitchDeckUrl,
      searchingCompanyId: investorSearch?.searchingCompanyId,
    }

    const result = await upsertInvestorSearch({
      variables: {
        input,
      },
    })

    if (result.errors) {
      alert(`Error: ${result.errors.toString()}`)
      setIsSubmitting(false)
    }

    tracker.investorMatchingBuilderSubmitted(input)

    const newInvestorSearch = result.data?.upsertInvestorSearch?.investorSearch
    if (newInvestorSearch?.investorSearchId) {
      router.push(`/investors/matching/${newInvestorSearch.investorSearchId}/processing`)
    } else {
      alert('Error: investorSearchId is empty.')
    }
  }

  const borderRadius = getBreakpointValue(['md', 'md', 'lg', 'xl', '2xl'])

  return (
    <Box
      bgColor={'colorPalette.container'}
      borderRadius={borderRadius}
      boxShadow={'lg'}
      width={'full'}
      {...props.containerBoxProps}
    >
      <TrackEvent action={'investorMatchingBuilderViewed'} onlyOnScreen={true} />
      {requireSignup && (props.emailCaptureMethod === 'modal' || !props.emailCaptureMethod) && (
        <Dialog.Root
          open={true}
          onOpenChange={({ open }) => {
            if (!open) {
              setRequireSignup(false)
              setIsSubmitting(false)
            }
          }}
        >
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content bgColor={'colorPalette.container'} padding={6}>
              <TrackEvent action={'investorMatchingEmailCaptureViewed'} onlyOnScreen={true} />
              <Heading marginBottom={6}>Register to See Investors</Heading>
              <UserSignupEmailCaptureForm
                hubspotFormKey={'scom-investor-matching-register'}
                afterSuccess={async () => {
                  await tracker.investorMatchingEmailCaptured()
                  onSubmit(true)
                }}
                containerProps={{
                  background: 'transparent',
                  boxShadow: 'none',
                  padding: 0,
                  border: 'none',
                }}
                submitButtonProps={{
                  size: 'lg',
                }}
                onSocialLoginClick={async () => {
                  await tracker.investorMatchingSocialLoginClicked()
                }}
              />
            </Dialog.Content>
          </Dialog.Positioner>
        </Dialog.Root>
      )}
      <SimpleGrid columns={getBreakpointValue([1, 1, 2])} gap={0} {...props.simpleGridProps}>
        <VStack align={'stretch'} borderLeftRadius={'2xl'} gap={0} order={isFormOnRight ? 1 : 0}>
          {!props.hide?.formTitle && (
            <Box
              bgColor={'colorPalette.muted'}
              paddingX={6}
              paddingY={4}
              {...(props.formPlacement === 'right'
                ? {
                    borderTopRightRadius: borderRadius,
                  }
                : {
                    borderTopLeftRadius: borderRadius,
                  })}
            >
              <Heading marginTop={1} size={getBreakpointValue(['md', 'md', 'lg'])}>
                {props.labels?.formTitle || 'Start a New Investor Match'}
              </Heading>
            </Box>
          )}

          {errorMessage && (
            <Box padding={6} paddingBottom={0}>
              <Alert.Root borderRadius={'md'} status={'error'}>
                <Alert.Indicator marginRight={'1em'} />
                <Box fontSize={'14px'}>{errorMessage}</Box>
              </Alert.Root>
            </Box>
          )}

          <VStack
            align={'stretch'}
            flexGrow={1}
            gap={4}
            padding={6}
            paddingBottom={8}
            {...props.formBoxProps}
          >
            {!props.hide?.fundingGoalInDollars && (
              <Field.Root>
                <Field.Label>
                  {props.labels?.fundingGoalInDollars || 'How much do you need to raise?'}
                </Field.Label>
                <InputGroup size={'lg'} startAddon={'USD $'}>
                  <Input asChild numericValue={fundingGoalInDollars} size={'lg'}>
                    <CurrencyInput
                      onNumericValueChange={value => {
                        setFundingGoalInDollars(value)
                      }}
                    />
                  </Input>
                </InputGroup>
              </Field.Root>
            )}

            {!props.hide?.companyName && (
              <Field.Root>
                <Field.Label>Startup Name:</Field.Label>
                <Input
                  placeholder={'Startup Name'}
                  type={'text'}
                  value={companyName}
                  onChange={e => setCompanyName(e.target.value)}
                />
              </Field.Root>
            )}

            {!props.hide?.companyElevatorPitch && (
              <Field.Root>
                <Field.Label>{props.labels?.companyName || 'Explain your startup:'}</Field.Label>
                <AutoResizeTextarea
                  fontSize={'sm'}
                  placeholder={'Elevator Pitch'}
                  value={companyElevatorPitch}
                  onChange={e => setCompanyElevatorPitch(e.target.value)}
                />
              </Field.Root>
            )}

            {!props.hide?.requiredInvestorTags && (
              <Field.Root>
                <Field.Label>Industries & Tags:</Field.Label>
                <InvestorTagsComboboxInput
                  deduplicateOptionsByTitle={true}
                  defaultValue={requiredInvestorTags}
                  investorTagTypeKeys={['investor_industry', 'investor_vertical']}
                  comboboxTagInputProps={{
                    allowMultipleSelections: true,
                    inputProps: {},
                  }}
                  onChange={items => setRequiredInvestorTags(items)}
                />
              </Field.Root>
            )}

            <Box paddingTop={2}>
              <Button
                disabled={upsertInvestorSearchMutation.loading || isSubmitting}
                loading={upsertInvestorSearchMutation.loading || isSubmitting}
                size={'lg'}
                variant={pitchDeckUrl ? 'outline' : 'cta'}
                width={'full'}
                onClick={() => {
                  tracker.investorMatchingBuilderSubmitClicked()
                  onSubmit()
                }}
              >
                See My Matches
                <LuArrowRight />
              </Button>
            </Box>
          </VStack>
        </VStack>
        {typeof props.renderContent === 'function' && !isModified ? (
          props.renderContent()
        ) : (
          <VStack
            align={'stretch'}
            bgColor={'purpleLightAlpha.100'}
            borderLeftRadius={props.formPlacement === 'right' ? borderRadius : null}
            borderRightRadius={props.formPlacement !== 'right' ? borderRadius : null}
            gap={0}
            height={'full'}
            {...props.contentBoxProps}
          >
            {!props.hide?.contentTitle && (
              <Box
                bgColor={'colorPalette.muted'}
                paddingX={6}
                paddingY={4}
                {...(props.formPlacement === 'right'
                  ? {
                      borderTopLeftRadius: borderRadius,
                    }
                  : {
                      borderTopRightRadius: borderRadius,
                    })}
              >
                <Heading
                  borderTopLeftRadius={props.formPlacement === 'right' ? borderRadius : null}
                  borderTopRightRadius={props.formPlacement !== 'right' ? borderRadius : null}
                  marginTop={1}
                  size={getBreakpointValue(['md', 'md', 'lg'])}
                >
                  {props.labels?.contentTitle || 'Optional: Let us do the work! ✨'}
                </Heading>
              </Box>
            )}
            <VStack
              align={'stretch'}
              borderRadius={'xl'}
              gap={4}
              padding={6}
              paddingBottom={8}
              {...props.contentInnerBoxProps}
            >
              <VStack borderRadius={'xl'} flexGrow={1} justify={'center'}>
                <VStack align={'stretch'} gap={6} padding={0}>
                  <Box>
                    <Text size={'md'}>
                      {'Upload your pitch deck or another'}{' '}
                      <Tooltip
                        background={'black'}
                        content={'Ex: executive summary, business plan, one-pager, etc.'}
                        maxWidth={'full'}
                        showArrow={true}
                        positioning={{
                          placement: 'top',
                        }}
                      >
                        <Link variant={'underline'}>{' similar document'}</Link>
                      </Tooltip>
                      . Our AI will automatically match investors and generate your outreach templates.
                    </Text>
                  </Box>
                  <VStack
                    backgroundColor={'whiteAlpha.100'}
                    border={'4px dashed'}
                    borderColor={'whiteAlpha.300'}
                    borderRadius={'md'}
                    gap={2}
                    paddingX={4}
                    paddingY={6}
                    textAlign={'center'}
                  >
                    {pitchDeckUrl && (
                      <Box>
                        <VStack bgColor={'blackAlpha.500'} borderRadius={'lg'} padding={4}>
                          <HStack>
                            <Icon asChild boxSize={6}>
                              <LuFileCheck />
                            </Icon>
                            <Text lineHeight={1} textAlign={'left'}>
                              <Link
                                flex={1}
                                fontSize={'md'}
                                href={pitchDeckUrl}
                                target={'_blank'}
                                variant={'underline'}
                                wordBreak={'break-word'}
                              >
                                {decodeURIComponent(pitchDeckUrl.split('/').pop())}
                              </Link>
                            </Text>

                            <IconButton
                              aria-label={'Remove File'}
                              marginLeft={4}
                              size={'xs'}
                              variant={'outline'}
                              onClick={() => setPitchDeckUrl(null)}
                            >
                              <LuX />
                            </IconButton>
                          </HStack>
                        </VStack>
                      </Box>
                    )}
                    {!pitchDeckUrl && (
                      <>
                        <HStack gap={4}>
                          <UploadDocumentButton
                            allowedExtensions={['pdf', 'doc', 'docx', 'pptx', 'ppt', 'txt']}
                            maxSizeInBytes={20000000}
                            size={'md'}
                            uploadUrl={scoBackendUrl('/assets/upload-pitch-deck')}
                            variant={'outlineGhost'}
                            storedFile={{
                              attachmentType: 'InvestorSearch',
                              attachmentId: investorSearch?.investorSearchId || null,
                              contextKey: 'pitch-deck',
                            }}
                            onError={err => alert(err?.toString() || 'Upload failed.')}
                            onSuccess={url => setPitchDeckUrl(url)}
                          >
                            <LuUpload />
                            Upload Document
                          </UploadDocumentButton>
                        </HStack>
                        <chakra.small color={'colorPalette.fg/50'} fontSize={'xs'}>
                          PDF, max 20MB.
                        </chakra.small>
                      </>
                    )}
                  </VStack>
                  <Box paddingTop={2}>
                    <Button
                      disabled={pitchDeckUrl && (upsertInvestorSearchMutation.loading || isSubmitting)}
                      loading={upsertInvestorSearchMutation.loading || isSubmitting}
                      size={'lg'}
                      variant={pitchDeckUrl ? 'cta' : 'outline'}
                      width={'full'}
                      onClick={() => {
                        tracker.investorMatchingBuilderSubmitClicked()
                        onSubmit()
                      }}
                    >
                      Analyze my Pitch Deck
                      <LuArrowRight />
                    </Button>
                  </Box>
                </VStack>
              </VStack>

              {!props.hide?.matchCount && investorSearch?.investorMatches?.pagination?.itemCount > 0 && (
                <Text
                  color={'white'}
                  fontFamily={'Poppins'}
                  fontSize={'0.9em'}
                  fontWeight={'bold'}
                  paddingTop={0}
                  textAlign={'center'}
                >
                  {'Good news! You have'}{' '}
                  <chakra.span
                    bgColor={'#C0553E'}
                    border={'1px solid'}
                    borderColor={'whiteAlpha.300'}
                    borderRadius={'full'}
                    boxShadow={'0px 0px 10px 0px #FF2E0080'}
                    color={'white'}
                    marginX={1}
                    paddingX={'0.5em'}
                    paddingY={'0.125em'}
                  >
                    <SSRGate disableSSR={true}>
                      {investorSearch?.investorMatches?.pagination?.itemCount || 0}
                    </SSRGate>
                  </chakra.span>{' '}
                  potential investor matches.
                </Text>
              )}

              {requireSignup && props.emailCaptureMethod === 'inline' && (
                <Presence
                  animationDuration={'moderate'}
                  present={true}
                  animationName={{
                    _open: 'slide-from-bottom, fade-in',
                    _closed: 'slide-to-bottom, fade-out',
                  }}
                >
                  <Box paddingTop={2}>
                    <Separator marginBottom={6} />
                    <Heading marginY={2} size={'lg'}>
                      First, let's create your free account.
                    </Heading>
                    <TrackEvent action={'investorMatchingEmailCaptureViewed'} onlyOnScreen={true} />
                    <UserSignupEmailCaptureForm
                      hubspotFormKey={'scom-investor-matching-register'}
                      afterSuccess={async (
                        values,
                        result: { data: { registerUser: RegisterUserPayload } }
                      ) => {
                        tracker.investorMatchingEmailCaptured()
                        if (result?.data?.registerUser?.loginTokenSent) {
                          alert(
                            'That email address was previously registered. Check your email account for an auto-login link to continue.'
                          )
                        } else {
                          setRequireSignup(false)
                          await onSubmit(true)
                        }
                      }}
                      containerProps={{
                        background: 'transparent',
                        boxShadow: 'none',
                        padding: 0,
                        border: 'none',
                      }}
                      submitButtonProps={{
                        children: 'See My Investors',
                      }}
                      onSocialLoginClick={() => {
                        tracker.investorMatchingSocialLoginClicked()
                        return true
                      }}
                    />
                  </Box>
                </Presence>
              )}
            </VStack>
          </VStack>
        )}
      </SimpleGrid>
    </Box>
  )
}
